import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Fallback from '@/components/Fallback';
import OpenGraph from '@/components/OpenGraph';
import MapContainer from '@/src/components/UI/MapContainer';
import { getOgDescription } from '@/src/libs/og-helper';
import serializer from '@/src/libs/serializer';
import { LISTING_STATUS } from '@/lib/constant';
import FunwooAPI, { ListingDetail } from '@/swagger';
import { GetStaticProps, NextPage } from 'next';
import { AxiosError } from 'axios';

interface Props {
  detailData: ReplaceValueType<ListingDetail, Date, number>;
}

const MapView: NextPage<Props> = ({ detailData }) => {
  const router = useRouter();
  if (router.isFallback) {
    return <Fallback />;
  }
  return (
    <>
      <Head>
        <script
          key={'googleMapSDK'}
          defer
          src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY}`}
        />
      </Head>
      <OpenGraph
        title={detailData.title || ''}
        description={getOgDescription(detailData)}
        type='funwoo.listing'
        url={`https://funwoo.com.tw/buy/${detailData.sid}`}
        imageUrl={detailData?.listing_image?.[0]?.watermark_image || ''}
      />
      <MapContainer detailData={detailData} />
    </>
  );
};

// noinspection JSUnusedGlobalSymbols
export async function getStaticPaths() {
  const { data: listings } = await FunwooAPI.listingApi.search({
    paging: {
      page: 1,
      pageSize: 99999,
    },
  });

  const paths = listings.payload.map((listing) => ({ params: { sid: listing.sid } }));
  return {
    paths,
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps<Props, { sid: string }> = async ({
  locale = '',
  params: { sid } = { sid: '' },
}) => {
  const { data: listing } = await FunwooAPI.listingApi.findOne(sid).catch((error: AxiosError) => {
    if (error.response?.status === 409) return { data: null };
    else throw error;
  });
  if (!listing || listing.status === LISTING_STATUS.INACTIVE) {
    return {
      notFound: true,
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    };
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      detailData: serializer(listing),
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    },
  };
};
export default MapView;
